import { api } from "./api";

export type RecoveryWindowStatus =
  | "open"
  | "stayed_with_current"
  | "switched"
  | "released"
  | "expired";

export interface RecoveryCounterOffer {
  id: string;
  items: Array<{ type: string; value?: number }>;
  commission_pct?: number | null;
  note?: string;
  expires_at: string;
}

export interface MarketplaceRecoveryWindow {
  id: string;
  role: string;
  target_kind: "active_offer" | "standing";
  target_ref: string;
  opened_at: string;
  expires_at: string;
  status: RecoveryWindowStatus;
  stay_kind?: string;
  closed_at?: string;
  proceed_state?: "idle" | "reserved" | "started";
  counter_offer?: RecoveryCounterOffer;
  target_pro_display_name?: string;
}

type WindowResponse = { window: MarketplaceRecoveryWindow | null };

export async function getCurrentRecoveryWindow(
  getToken: () => Promise<string | null>,
): Promise<MarketplaceRecoveryWindow | null> {
  const token = await getToken();
  const response = await api.get<WindowResponse>(
    "/api/marketplace/recovery-windows/current",
    token ?? undefined,
  );
  return response.window;
}

export async function openRecoveryWindow(
  getToken: () => Promise<string | null>,
  targetKind: "active_offer" | "standing",
  targetRef: string,
): Promise<MarketplaceRecoveryWindow> {
  const token = await getToken();
  const response = await api.post<WindowResponse>(
    "/api/marketplace/recovery-windows",
    { role: "agent", target_kind: targetKind, target_ref: targetRef },
    token ?? undefined,
  );
  if (!response.window) throw new Error("Recovery window was not returned");
  return response.window;
}

export async function stayWithCurrentAgent(
  getToken: () => Promise<string | null>,
  windowId: string,
): Promise<MarketplaceRecoveryWindow> {
  const token = await getToken();
  const response = await api.post<WindowResponse>(
    `/api/marketplace/recovery-windows/${encodeURIComponent(windowId)}/stay`,
    { stay_kind: "retention_accept" },
    token ?? undefined,
  );
  if (!response.window) throw new Error("Recovery window was not returned");
  return response.window;
}

export async function continueWithMarketplaceChoice(
  getToken: () => Promise<string | null>,
  windowId: string,
): Promise<void> {
  const token = await getToken();
  await api.post(
    `/api/marketplace/recovery-windows/${encodeURIComponent(windowId)}/proceed`,
    {},
    token ?? undefined,
  );
}
